import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Label } from './ui/label';
import { DropLineup } from './DropLineup';
import { DropPushPreview } from './DropPushPreview';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const opts = { withCredentials: true };
const fmt = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });

function Toggle({ on, busy, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} disabled={busy} aria-pressed={on} data-testid={testId}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-none border transition-colors disabled:opacity-50 ${on ? 'bg-[#FF9F0A] border-[#FF9F0A]' : 'bg-[#3A2A48] border-[#6B8E73]/50'}`}>
      <span className={`inline-block h-4 w-4 transform bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

const daysLeft = (iso, expire) => Math.max(0, Math.ceil((new Date(iso).getTime() + expire * 86400000 - Date.now()) / 86400000));

function RegionDrop({ productId, region, on, busy, onFlip }) {
  const [lineup, setLineup] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API_URL}/api/drop/lineup/${region}`, opts).then(({ data }) => setLineup(data)).catch(() => setLineup(null));
  }, [region]);
  useEffect(() => { load(); }, [load, on]);
  useEffect(() => {
    const onSaved = (e) => { if (e.detail === region) load(); };
    window.addEventListener('drop-settings-saved', onSaved);
    return () => window.removeEventListener('drop-settings-saved', onSaved);
  }, [load, region]);
  const expire = lineup?.expire_days || 0;
  return (
    <div className="space-y-2" data-testid={`drop-row-${region}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[#FFD9A0] font-mono text-xs uppercase">{region}</p>
          <p className="text-[#6B8E73] font-mono text-[10px]" data-testid={`drop-status-${region}`}>
            {on ? `// on since ${fmt(on)}${expire ? ` · auto-off in ${daysLeft(on, expire)} day(s)` : ''}` : '// off'}
          </p>
        </div>
        <Toggle on={!!on} busy={busy} onClick={onFlip} testId={`drop-toggle-${region}`} />
      </div>
      {lineup && <DropLineup items={lineup.items} productId={productId} region={region} />}
    </div>
  );
}

// Per-region The Drop switches for a saved product. Saves instantly.
export function DropToggles({ productId, regions, initial, onChange }) {
  const [drop, setDrop] = useState(initial || {});
  const [busy, setBusy] = useState(null);
  const [preview, setPreview] = useState(null);
  const flip = async (region) => {
    setBusy(region);
    try {
      const { data } = await axios.patch(`${API_URL}/api/products/${productId}/drop`, { region, on: !drop[region] }, opts);
      setDrop(data.region_drop);
      onChange?.(data.region_drop);
      const on = !!data.region_drop[region];
      toast.success(`The Drop · ${region.toUpperCase()}: ${on ? 'ON (moved to top)' : 'OFF'}`);
      if (on) setPreview({ productId, regions: [region] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update The Drop');
    } finally { setBusy(null); }
  };
  return (
    <div className="space-y-4">
      {regions.map((r) => <RegionDrop key={r} productId={productId} region={r} on={drop[r]} busy={busy === r} onFlip={() => flip(r)} />)}
      <DropPushPreview target={preview} onDone={() => setPreview(null)} />
    </div>
  );
}

export function DropSection({ children, subtitle, action }) {
  return (
    <div className="bg-[#0C0612] border border-[#FF9F0A]/40 p-3 space-y-3" data-testid="featured-toggle-row">
      <div className="flex items-start justify-between gap-2">
        <div>
          <Label className="text-[#FF9F0A] font-mono text-xs uppercase tracking-wider">⭐ FEATURED — THE DROP</Label>
          <p className="text-[#6B8E73] font-mono text-[10px] mt-1">{subtitle}</p>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

export { Toggle as DropSwitch };
